<template>
  <div v-if="isOpen" class="modal-overlay" @click="closeModal">
    <div class="gm-panel gm-panel--raised modal-content" @click.stop>
      <div class="header">
        <div class="header-top">
          <h2 class="gm-title title">Select Character (GM Mode)</h2>
          <button class="gm-btn gm-btn--sm gm-btn--ghost close-btn" @click="closeModal" title="Close Character Selector">×</button>
        </div>

        <!-- Filter Controls -->
        <div class="filters">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search characters..."
            class="gm-input search-input"
          />
          <select v-model="selectedNpcFilter" class="gm-select filter-select">
            <option value="">All Characters</option>
            <option value="false">Players Only</option>
            <option value="true">NPCs Only</option>
          </select>
          <label v-if="currentPositionId" class="position-filter-checkbox">
            <input
              type="checkbox"
              v-model="filterByCurrentPosition"
            />
            <span>Current Position</span>
          </label>
          <button
            @click="clearAllFilters"
            class="gm-btn gm-btn--sm clear-all-btn"
            :disabled="activeFilters.length === 0"
            title="Clear all filters"
          >
            Clear All
          </button>
        </div>

        <!-- Active Filter Tags -->
        <div v-if="activeFilters.length > 0" class="filter-tags">
          <div class="filter-tags-label">Active Filters:</div>
          <div class="filter-tags-list">
            <div
              v-for="filter in activeFilters"
              :key="filter.type"
              class="gm-chip filter-tag"
            >
              <span class="filter-tag-label">{{ filter.label }}</span>
              <button
                @click="removeFilter(filter.type)"
                class="filter-tag-remove"
                title="Remove this filter"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="gm-scroll modal-body">
        <div v-if="loading" class="loading-state">
          <span class="dx-loader" aria-hidden="true"></span>
          <p>Loading characters...</p>
        </div>

        <div v-else-if="error" class="error-state">
          <p class="error-message">{{ error }}</p>
          <button class="retry-btn" @click="loadCharacters">Retry</button>
        </div>

        <div v-else-if="filteredCharacters.length === 0" class="empty-state">
          <p>No characters found matching your criteria.</p>
          <button class="clear-filters-btn" @click="clearAllFilters">
            Clear Filters
          </button>
        </div>

        <div v-else class="character-grid">
          <div
            v-for="character in filteredCharacters"
            :key="character.id"
            class="gm-panel character-card-wrapper"
            @click="selectCharacter(character.id)"
          >
            <div class="character-avatar-container">
              <img
                :src="avatarFor(character)"
                :alt="character.name"
                class="character-avatar"
                :class="{ 'dead': character.health_current === 0 }"
                @error="handleImageError(character)"
              />
              <div v-if="character.health_current === 0" class="dead-overlay"></div>
              <div v-if="character.is_npc" class="npc-badge">NPC</div>
            </div>
            <div class="character-info">
              <div class="character-name">{{ character.name }}</div>
              <div class="character-stats">
                <div class="stat-bar health-bar">
                  <div class="stat-label">HP</div>
                  <div class="stat-track">
                    <div
                      class="stat-fill health-fill"
                      :style="{ width: getHealthPercent(character) + '%' }"
                    ></div>
                  </div>
                  <div class="stat-value">{{ character.health_current }}/{{ character.health_max }}</div>
                </div>
                <div class="stat-bar energy-bar">
                  <div class="stat-label">EN</div>
                  <div class="stat-track">
                    <div
                      class="stat-fill energy-fill"
                      :style="{ width: getEnergyPercent(character) + '%' }"
                    ></div>
                  </div>
                  <div class="stat-value">{{ character.energy_current }}/{{ character.energy_max }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import GMCharacterSwitchService, { type GMAvailableCharacter } from '@/services/GMCharacterSwitchService';
import avatarPlaceholder from '@/assets/images/avatar/avatarplaceholder.png';

const props = defineProps<{
  isOpen: boolean;
  currentPositionId?: string;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'characterSelected', characterId: string): void;
}>();

const loading = ref(false);
const error = ref<string | null>(null);
const characters = ref<GMAvailableCharacter[]>([]);

// Filter state
const searchQuery = ref('');
const selectedNpcFilter = ref('');
const filterByCurrentPosition = ref(true); // Default to filtering by current position

// Get unique positions from characters
const positions = computed(() => {
  const posSet = new Set<string>();
  characters.value.forEach(char => {
    if (char.position_id) {
      posSet.add(char.position_id);
    }
  });
  return Array.from(posSet);
});

// Filtered characters based on search and filters (position filtering done on backend)
const filteredCharacters = computed(() => {
  let filtered = characters.value;

  // Apply search query (name, id)
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase().trim();
    filtered = filtered.filter(char =>
      char.name.toLowerCase().includes(query) ||
      char.id.toLowerCase().includes(query)
    );
  }

  // Apply NPC filter
  if (selectedNpcFilter.value !== '') {
    const isNpc = selectedNpcFilter.value === 'true';
    filtered = filtered.filter(char => char.is_npc === isNpc);
  }

  return filtered;
});

// Active filters computed property
const activeFilters = computed(() => {
  const filters = [];

  if (filterByCurrentPosition.value && props.currentPositionId) {
    filters.push({
      type: 'position',
      label: 'Current Position Only',
      value: 'position'
    });
  }

  if (searchQuery.value.trim()) {
    filters.push({
      type: 'search',
      label: `Search: "${searchQuery.value}"`,
      value: searchQuery.value
    });
  }

  if (selectedNpcFilter.value !== '') {
    const label = selectedNpcFilter.value === 'true' ? 'NPCs Only' : 'Players Only';
    filters.push({
      type: 'npc',
      label: label,
      value: selectedNpcFilter.value
    });
  }

  return filters;
});

// Clear all filters function
const clearAllFilters = () => {
  searchQuery.value = '';
  selectedNpcFilter.value = '';
  filterByCurrentPosition.value = false;
};

// Individual filter removal functions
const removeFilter = (filterType: string) => {
  switch (filterType) {
    case 'search':
      searchQuery.value = '';
      break;
    case 'npc':
      selectedNpcFilter.value = '';
      break;
    case 'position':
      filterByCurrentPosition.value = false;
      break;
  }
};

// Helper methods
const getHealthPercent = (character: GMAvailableCharacter): number => {
  if (character.health_max === 0) return 0;
  return Math.round((character.health_current / character.health_max) * 100);
};

const getEnergyPercent = (character: GMAvailableCharacter): number => {
  if (character.energy_max === 0) return 0;
  return Math.round((character.energy_current / character.energy_max) * 100);
};

// Reassigning the failed <img>'s src to a fallback is what made this loop
// forever: the fallback was a path that does not exist, so its own 404 fired
// `error` again and the handler set the same dead URL back. The broken avatar
// is remembered instead, the src is derived from that, and the placeholder is
// a bundled asset the build guarantees — an unchanged src cannot re-fire.
const brokenAvatars = reactive(new Set<string>());

const avatarFor = (character: GMAvailableCharacter): string =>
  character.avatar && !brokenAvatars.has(character.id) ? character.avatar : avatarPlaceholder;

const handleImageError = (character: GMAvailableCharacter) => {
  if (brokenAvatars.has(character.id)) return;
  brokenAvatars.add(character.id);
  console.warn(`[GMCharacterSelectorModal] avatar failed for ${character.name}, using the placeholder`);
};

// Fetch characters when modal opens or filter changes
watch(() => props.isOpen, async (isOpen) => {
  if (isOpen) {
    await loadCharacters();
  }
});

// Reload characters when position filter changes
watch(filterByCurrentPosition, async () => {
  if (props.isOpen) {
    await loadCharacters();
  }
});

const loadCharacters = async () => {
  loading.value = true;
  error.value = null;

  try {
    // Pass position_id to backend if filter is enabled
    const positionIdParam = (filterByCurrentPosition.value && props.currentPositionId)
      ? props.currentPositionId
      : undefined;

    console.debug(
      `[GMCharacterSelectorModal] Loading characters (positionId=${positionIdParam || 'none'})`
    );

    characters.value = await GMCharacterSwitchService.getAvailableCharacters(positionIdParam);
  } catch (err) {
    console.error('[GMCharacterSelectorModal] Error loading characters:', err);
    error.value = 'Failed to load characters. Please try again.';
  } finally {
    loading.value = false;
  }
};

const selectCharacter = (characterId: string) => {
  console.info(`[GMCharacterSelectorModal] Character selected: ${characterId}`);
  emit('characterSelected', characterId);
};

const closeModal = () => {
  console.debug('[GMCharacterSelectorModal] Closing modal');
  emit('close');
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.modal-content {
  max-width: 900px;
  width: 90%;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.header {
  flex-shrink: 0;
  margin-bottom: var(--gm-gap);
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--gm-gap);
  position: relative;
}

.title {
  flex: 1;
  text-align: center;
}

.close-btn {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  border-radius: 50%;
  width: 2rem;
  height: 2rem;
  padding: 0;
}

.filters {
  display: flex;
  gap: var(--gm-gap-sm);
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
}

.search-input,
.filter-select {
  flex: 1;
  min-width: 140px;
}

.clear-all-btn {
  flex: 0 0 auto;
}

.position-filter-checkbox {
  display: flex;
  align-items: center;
  gap: var(--gm-gap-sm);
  padding: 0.45rem 0.6rem;
  color: var(--gm-text-muted);
  font-family: var(--gm-font-body);
  font-size: 0.85rem;
  cursor: pointer;
  user-select: none;
}

.position-filter-checkbox input[type="checkbox"] {
  width: 1.1rem;
  height: 1.1rem;
  cursor: pointer;
  accent-color: var(--gm-accent-hi);
}

/* Filter Tags */
.filter-tags {
  margin-top: var(--gm-gap-sm);
  padding-top: var(--gm-gap-sm);
  border-top: 1px solid var(--gm-border-dim);
}

.filter-tags-label {
  font-family: var(--gm-font-display);
  font-size: 0.6rem;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--gm-text-muted);
  margin-bottom: var(--gm-gap-xs);
  text-align: center;
}

.filter-tags-list {
  display: flex;
  flex-wrap: wrap;
  gap: var(--gm-gap-xs);
  justify-content: center;
}

.filter-tag-label {
  margin-right: 0.25rem;
  font-weight: 400;
}

.filter-tag-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--gm-accent);
  font-size: 0.75rem;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.15s ease;
  line-height: 1;
}

.filter-tag-remove:hover {
  background: rgba(201, 69, 63, 0.2);
  color: var(--gm-danger);
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: var(--gm-gap);
  display: flex;
  flex-direction: column;
}

.loading-state,
.error-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--gm-gap-lg);
  color: var(--gm-text-muted);
  flex: 1;
}

.error-message {
  color: var(--gm-danger);
  margin-bottom: var(--gm-gap);
  font-size: 1rem;
}

.retry-btn,
.clear-filters-btn {
  margin-top: var(--gm-gap);
}

.character-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: var(--gm-gap-sm);
  padding: 0.5rem;
  width: 100%;
}

.character-card-wrapper {
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
  display: flex;
  flex-direction: column;
  padding: 0;
}

.character-card-wrapper:hover {
  border-color: var(--gm-border);
  background: var(--gm-accent-soft);
}

.character-avatar-container {
  position: relative;
  width: 100%;
  padding-top: 100%; /* 1:1 Aspect Ratio */
  overflow: hidden;
  background: rgba(0, 0, 0, 0.5);
}

.character-avatar {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: filter 0.3s ease;
}

.character-avatar.dead {
  filter: grayscale(100%);
  opacity: 0.6;
}

.dead-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url('@/assets/images/avatar/death.webp') no-repeat center center;
  background-size: contain;
  opacity: 0.7;
  pointer-events: none;
}

.npc-badge {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: var(--gm-surface-0);
  color: var(--gm-accent);
  padding: 0.2rem 0.45rem;
  border-radius: var(--gm-radius-sm);
  font-size: 0.6rem;
  font-weight: bold;
  font-family: var(--gm-font-display);
  border: 1px solid var(--gm-border);
  letter-spacing: 0.05em;
  z-index: 5;
}

.character-info {
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.character-name {
  font-family: var(--gm-font-display);
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--gm-accent);
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.character-stats {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.stat-bar {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.65rem;
}

.stat-label {
  font-weight: 600;
  color: var(--gm-text-muted);
  min-width: 1.2rem;
  text-align: right;
  font-size: 0.6rem;
}

.stat-track {
  flex: 1;
  height: 0.4rem;
  background: var(--gm-surface-sunken);
  border: 1px solid var(--gm-border-dim);
  border-radius: 0.2rem;
  overflow: hidden;
  position: relative;
}

.stat-fill {
  height: 100%;
  transition: width 0.3s ease;
  border-radius: 0.2rem;
}

.health-fill {
  background: var(--gm-danger);
}

.energy-fill {
  background: var(--gm-info);
}

.stat-value {
  font-size: 0.6rem;
  color: var(--gm-text-muted);
  min-width: 2.5rem;
  text-align: right;
}

/* Responsive */
@media (max-width: 768px) {
  .filters {
    flex-direction: column;
    gap: 0.5rem;
  }

  .search-input,
  .filter-select {
    min-width: 100%;
  }

  .character-grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 0.75rem;
  }

  .character-name {
    font-size: 0.9rem;
  }

  .stat-bar {
    font-size: 0.7rem;
  }
}

@media (max-width: 480px) {
  .character-grid {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
}
</style>
